import representantApi, {
  REPRESENTANT_TOKEN_KEY,
  REPRESENTANT_DATA_KEY,
} from './representant-axios';

/**
 * Service d'authentification de l'espace professionnel (membres de cabinet).
 */

export interface MembreCabinet {
  id: string;
  cabinetId: string;
  civilite?: string;
  nom: string;
  prenom: string;
  email: string;
  telephone?: string;
  roleMembre: string;
  numeroOrdre?: string;
  estTitulaire: boolean;
  statut: string;
  doitChangerMotDePasse: boolean;
  derniereConnexion?: string;
}

export interface CabinetInfo {
  id: string;
  numeroCabinet: string;
  corps: string;
  nom: string;
  forme?: string;
  email?: string;
  telephone?: string;
  adresse?: string;
  ville?: string;
  barreau?: string;
  statut: string;
}

export interface RepresentantSession {
  membre: MembreCabinet;
  cabinet: CabinetInfo | null;
}

export interface LoginResultat extends RepresentantSession {
  access_token: string;
  doitChangerMotDePasse: boolean;
}

/**
 * Profils standards d'un cabinet d'avocats.
 *
 * 3 catégories :
 *  - Avocats (statuts) : peuvent être responsables d'un dossier
 *  - Supports juridiques : saisie, recherches, formalités
 *  - Direction & gestion : administration, gestion, communication, finances
 */
export const ROLE_MEMBRE_LABELS: Record<string, string> = {
  // Avocats (statuts)
  ASSOCIE: 'Associé',
  COUNSEL: 'Counsel / Of Counsel',
  COLLABORATEUR: 'Collaborateur',
  STAGIAIRE: 'Avocat stagiaire',
  // Supports juridiques
  JURISTE: 'Juriste',
  CLERC: "Clerc d'avocat",
  FORMALISTE: 'Formaliste',
  SECRETAIRE: 'Assistant / Secrétaire juridique',
  // Direction & gestion
  OFFICE_MANAGER: 'Office Manager',
  COMPTABLE: 'Comptable',
  DOCUMENTALISTE: 'Documentaliste / Veille juridique',
  COMMUNITY_MANAGER: 'Community Manager',
  // Compatibilité ascendante (anciens comptes)
  TITULAIRE: 'Associé (titulaire)',
  AVOCAT_COLLABORATEUR: 'Collaborateur',
  AVOCAT_STAGIAIRE: 'Avocat stagiaire',
};

/** Rôles considérés comme avocats — peuvent être avocat responsable d'un dossier. */
export const ROLES_AVOCAT = [
  'ASSOCIE',
  'COUNSEL',
  'COLLABORATEUR',
  'STAGIAIRE',
  'TITULAIRE',
  'AVOCAT_COLLABORATEUR',
  'AVOCAT_STAGIAIRE',
] as const;

/** Rôles autorisés à créer un dossier. */
export const ROLES_CREATEUR_DOSSIER = [
  ...ROLES_AVOCAT,
  'SECRETAIRE',
  'JURISTE',
  'CLERC',
  'FORMALISTE',
  'OFFICE_MANAGER',
] as const;

/** Catégories pour grouper les rôles dans les selects. */
export const ROLES_PAR_CATEGORIE: Array<{
  categorie: string;
  roles: string[];
}> = [
  {
    categorie: 'Avocats',
    roles: ['ASSOCIE', 'COUNSEL', 'COLLABORATEUR', 'STAGIAIRE'],
  },
  {
    categorie: 'Supports juridiques',
    roles: ['JURISTE', 'CLERC', 'FORMALISTE', 'SECRETAIRE'],
  },
  {
    categorie: 'Direction & gestion',
    roles: ['OFFICE_MANAGER', 'COMPTABLE', 'DOCUMENTALISTE', 'COMMUNITY_MANAGER'],
  },
];

export const estAvocat = (role?: string) =>
  !!role && ROLES_AVOCAT.includes(role as any);

export const peutCreerDossier = (role?: string) =>
  !!role && ROLES_CREATEUR_DOSSIER.includes(role as any);

class RepresentantAuthService {
  async login(
    identifiant: string,
    motDePasse: string,
    numeroOrdre?: string,
  ): Promise<LoginResultat> {
    const { data } = await representantApi.post('/auth/representant/login', {
      identifiant,
      motDePasse,
      numeroOrdre,
    });
    localStorage.setItem(REPRESENTANT_TOKEN_KEY, data.access_token);
    localStorage.setItem(
      REPRESENTANT_DATA_KEY,
      JSON.stringify({ membre: data.membre, cabinet: data.cabinet }),
    );
    return data;
  }

  async me(): Promise<RepresentantSession> {
    const { data } = await representantApi.get('/auth/representant/me');
    localStorage.setItem(REPRESENTANT_DATA_KEY, JSON.stringify(data));
    return data;
  }

  async changePassword(
    ancienMotDePasse: string,
    nouveauMotDePasse: string,
  ): Promise<{ success: boolean; message: string }> {
    const { data } = await representantApi.post(
      '/auth/representant/change-password',
      { ancienMotDePasse, nouveauMotDePasse },
    );
    return data;
  }

  async forgotPassword(email: string): Promise<void> {
    await representantApi.post('/auth/representant/forgot-password', { email });
  }

  async resetPassword(
    token: string,
    nouveauMotDePasse: string,
  ): Promise<void> {
    await representantApi.post('/auth/representant/reset-password', {
      token,
      nouveauMotDePasse,
    });
  }

  logout(): void {
    localStorage.removeItem(REPRESENTANT_TOKEN_KEY);
    localStorage.removeItem(REPRESENTANT_DATA_KEY);
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem(REPRESENTANT_TOKEN_KEY);
  }

  private getSession(): RepresentantSession | null {
    const raw = localStorage.getItem(REPRESENTANT_DATA_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  getMembre(): MembreCabinet | null {
    return this.getSession()?.membre || null;
  }

  getCabinet(): CabinetInfo | null {
    return this.getSession()?.cabinet || null;
  }
}

const representantAuthService = new RepresentantAuthService();
export default representantAuthService;
